<script lang="ts" setup>
  import { reactive, computed, onMounted, onBeforeUnmount, watch } from 'vue'

  import './wai-panel.css'

  const STORAGE_KEY = 'WAI-PANEL'

  /**
   * Панель WAI управляет только наборами
   * классов и больше ничего. Основной класс добавляется к html.
   *
   * Классы wai и wai-cloak используются для работы компонента
   * или скрипта. Vue компонент - WaiPanel (src/component/wai/*).
   *
   * - wai - корневой класс, добавляется вручную к html.
   * - wai-cloak - для скрытия или блокировки некоторых стилей,
   *   добавляется вручную к html, затем управляется динамически.
   * - wai-active - динамически добавляется к `html` компонентом
   * - wai-wrapper - динамически добавляется к `body`
   *
   * Неактивный режим
   *
   * ```html
   * <html class="wai wai-cloak">
   *    <body></body>
   * </html>
   * ```
   *
   * Активный режим
   *
   * ```html
   * <html class="wai wai-active wai-font-* wai-theme-* и т.д.">
   *    <body class="wai-wrapper"></body>
   * </html>
   * ```
   *
   * @todo wai-panel fixed
   *
  */

  // Инициализируем состояние.
  // state.active управляет тем, включена ли "Версия для слабовидящих"
  const state = reactive({
    font: 'wai-font-sm',
    theme: 'wai-theme-white',
    images: 'wai-images-false',
    active: false
  })

  const fonts = [
    { value: 'wai-font-sm', title: 'Стандартный шрифт' },
    { value: 'wai-font-md', title: 'Средний шрифт' },
    { value: 'wai-font-lg', title: 'Большой' }
  ]

  const themes = [
    { value: 'wai-theme-white', title: 'белый фон' },
    { value: 'wai-theme-black', title: 'черный фон' },
    { value: 'wai-theme-blue', title: 'голубой фон' }
  ]

  const images = [
    {
      value: 'images-true',
      title: 'Цветные изображения',
      icon: 'i-lucide-image'
    },
    {
      value: 'images-grayscale',
      title: 'Черно-белые изображения',
      icon: 'i-lucide-contrast'
    },
    {
      value: 'wai-images-false',
      title: 'Скрыть изображения',
      icon: 'i-lucide-image-off'
    }
  ]

  const bodyClasses = computed(() => [state.font, state.theme])

  function applyHtmlClasses() {
    const html = document.documentElement

    /**
     * Наиболее простой способ это удалить все классы
     * и добавить новый измененный набор.
     */
    const allFontClasses = fonts.map(f => f.value)
    const allThemeClasses = themes.map(t => t.value)
    const allImageClasses = images.map(i => i.value)

    html.classList.remove(...allFontClasses, ...allThemeClasses, ...allImageClasses, 'wai-active')
    html.getElementsByTagName('body')[0].classList.remove('wai-wrapper')

    /**
     * При активации режима добавляются классы.
     */
    if (state.active) {
      html.classList.add('wai-active')

      /**
       * По умолчанию body служит контейнером .wai-wrapper
       */
      html.getElementsByTagName('body')[0].classList.add('wai-wrapper')

      html.classList.add(state.font)
      html.classList.add(state.theme)
      html.classList.add(state.images)
    }
  }

  export type WaiSelectorType = 'font' | 'theme' | 'images' | 'open' | 'close'

  function waiChange(type: WaiSelectorType , value?: string) {
    if (type === 'font' && value) state.font = value
    if (type === 'theme' && value) state.theme = value
    if (type === 'images' && value) state.images = value

    if (type === 'open') {
      state.active = true

      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    }

    if (type === 'close') state.active = false

    save()
  }

  const WAI_OPEN_EVENT = 'wai:open'

  function handleExternalOpen() {
    waiChange('open')
  }

  function save() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch (e) {
      console.error('Save error:', e)
    }
  }

  function restore() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)

      if (saved) {
        const parsed = JSON.parse(saved)
        // Используем Object.assign для сохранения реактивности
        Object.assign(state, parsed)
      }

      // Убираем скрытие контента после загрузки настроек
      document.documentElement.classList.remove('wai-cloak')
      applyHtmlClasses()
    } catch (e) {
      console.error('Restore error:', e)
    }
  }

  onMounted(() => {
    restore()

    window.addEventListener(
      WAI_OPEN_EVENT,
      handleExternalOpen
    )
  })

  onBeforeUnmount(() => {
    window.removeEventListener(
      WAI_OPEN_EVENT,
      handleExternalOpen
    )
  })

  // Следим за изменениями и обновляем DOM
  watch(
    () => [state.font, state.theme, state.images, state.active],
    () => applyHtmlClasses()
  )

// тестовые вид кнопки
const params = new URLSearchParams(window.location.search)

const waiThemeClass = computed(() => {
  if (params.get('v') === '1') return 'theme-v1'
  if (params.get('v') === '2') return 'theme-v2'
  if (params.get('v') === '3') return 'theme-v3'
  return ''
})

const waiIconClass = computed(() => {
  if (params.get('v') === '1') return 'i-lucide-eye'
  if (params.get('v') === '2') return 'i-lucide-person-standing'
  if (params.get('v') === '3') return 'i-lucide-eye'
  return 'i-lucide-eye'
})
</script>

<template>
  <!-- Панель управления (показывается, когда режим активен) -->
  <div
    class="section wai-panel"
    :class="bodyClasses"
    v-if="state.active"
    role="toolbar"
    aria-label="Панель версии для слабовидящих"
  >
    <div class="container">
      <div class="wai-panel-container">
        <div
          class="wai-panel-item wai-fonts-panel has-text-centered"
          role="group"
          aria-label="Настройка шрифта"
          >
          <div class="wai-fonts">
            <p class="heading">
              Шрифт
            </p>

            <div class="field">
              <button
                v-for="font in fonts"
                type="button"
                href="#"
                :key="font.value"
                class="button is-medium wai-font-button"
                :class="[font.value, { active: state.font === font.value }]"
                @click.prevent="waiChange('font', font.value)"
                :aria-pressed="state.font === font.value"
                :aria-label="font.title"
              >
                <span class="wai-font-label">
                  A
                </span>
              </button>
            </div>
          </div>
        </div>

        <div
          class="wai-panel-item wai-themes-panel has-text-centered"
          role="group"
          aria-label="Настройка цветовой схемы"
          >
          <div class="wai-themes">
            <p class="heading">
              Схема
            </p>
            <div class="field">
              <button
                v-for="theme in themes"
                type="button"
                :key="theme.value"
                class="button is-medium wai-theme-button"
                :class="[theme.value, { active: state.theme === theme.value }]"
                @click.prevent="waiChange('theme', theme.value)"
                :aria-pressed="state.theme === theme.value"
                :aria-label="theme.title"
              >
                <span class="wai-font-label">
                  T
                </span>
              </button>
            </div>
          </div>
        </div>

        <div
          class="wai-panel-item wai-images-panel has-text-centered"
          role="group"
          aria-label="Настройка отображения изображений"
          >
          <div class="wai-images">
            <p class="heading">
              Изображения
            </p>

            <div class="field">
              <button
                v-for="image in images"
                type="button"
                :key="image.value"
                class="button is-medium wai-image-button"
                :class="[image.value, { active: state.images === image.value }]"
                @click.prevent="waiChange('images', image.value)"
                :aria-pressed="state.images === image.value"
                :aria-label="image.title"
                >
                <span class="wai-image-label">
                  <i :class=image.icon />
                </span>
              </button>
            </div>
          </div>
        </div>

        <div
          class="wai-panel-item wai-close-panel has-text-centered"
          role="group"
          aria-label="Выход из версии для слабовидящих"
        >
          <div class="wai-close">
            <div class="field">
              <button
                type="button"
                class="button is-medium wai-close-button has-text-black"
                @click.prevent="waiChange('close')"
                aria-label="Вернуться к обычной версии сайта"
              >
                Обычная версия
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Кнопка включения (показывается, когда режим выключен) -->
  <div
    class="wai-sidenav-button"
    :class="waiThemeClass"
    v-if="!state.active"
    >
    <button
      type="button"
      class="wai-open-button"
      @click="waiChange('open')"
      aria-label="Открыть версию для слабовидящих"
    >
      <span class="text">Версия для слабовидящих</span>
      <span class="image-wai" aria-hidden="true" >
        <i :class="waiIconClass" />
      </span>
    </button>
  </div>
</template>
